import { type HeightSettings, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  type PropertiesConfig,
  type PropertiesConfigInputFontStyleOptions,
} from '@blaze-shared/elements-domain';
import {
  findWorkflowNode,
  getValueByPath,
  isAggregateNode,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { getIoConfig } from './_base-chart.element';
import { ValueChartDefinitionMapper } from './ai/value-chart.definition-mapper';
import { ValueChartElementHandler } from './ai/value-chart.element-handler';
import { ChartComponent } from './chart.component';
import {
  AGGREGATION_OPTIONS,
  CHART_DIMENSIONS,
  type ChartPropsModel,
  type ChartValuePropsModel,
  NUMBER_FIELD_FORMATS,
} from './chart.models';
import { ChartSinglePropsBuilder } from './chart.props-builder';
import { ChartSinglePropsExtractor } from './chart.props-extractor';
import { singleChartV2Migration } from './migrations/002-single-chart.migration';
import { chartV3Migration } from './migrations/003-chart.migration';
import { preventSelectingColumnForCount } from './utils/chart-select-options.utils';

const SINGLE_CHART_DIMENSIONS = {
  width: 230,
  height: CHART_DIMENSIONS.height / 2,
  minSize: 45,
  maxWidth: 900,
  maxHeight: 900,
  widthOptions: ['fixed', 'full'] as ElementSizingMode[],
  heightOptions: ['min'] as ElementSizingMode[],
};

export function getChartSingleDefinition(): CanvasElementDefinition<'chartSingle'> {
  return {
    type: 'chartSingle',
    componentType: ChartComponent<'chartSingle'>,
    isListed: true,
    name: 'Number',
    width: { mode: 'value', value: SINGLE_CHART_DIMENSIONS.width, fixed: true },
    height: { mode: 'value', value: SINGLE_CHART_DIMENSIONS.height, fixed: false },
    ioConfig: getIoConfig(),
    propsConfig: getPropsConfig(),
    propsBuilder: ChartSinglePropsBuilder,
    propsExtractor: ChartSinglePropsExtractor,
    defaults: getDefaults(),
    initial: getInitial(),
    toolbar: ['triggerClick'],
    groups: ['charts'],
    tags: ['chart'], // Added to make search work after the word 'chart' (as it is in the chart group)
    styleIncludePaths: [
      'style',
      'content.font.fontFamily',
      'content.font.style',
      'content.font.fontSize',
      'content.font.fontWeight',
      'content.title.iconPosition',
    ],
    features: {
      ai: {
        showAiPromptAutomatically: true,
        description: true,
      },
      visualStyle: 'block',
    },

    version: 3,
    migrations: {
      2: singleChartV2Migration,
      3: chartV3Migration,
    },
    ai: {
      name: 'number',
      handler: () => ValueChartElementHandler,
      definitionMapper: () => ValueChartDefinitionMapper,
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('chartSingle', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'data.?key:value.enabled',
        '',
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data Source',
            sources: 'list',
            bindings: [],
          }),
        ],
        {
          labelWidth: '40%',
          hideLabel: true,
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'Show',
        [
          // BL-2584
          // epc.utils.observedField(
          epc.fields.selectDataValueField(
            'Value',
            ['content.data.aggregation', 'content.data.mainValue'],
            [AGGREGATION_OPTIONS, 'data.?key:value.dataSource'],
            {
              dataBindingsMode: 'first-dataset',
              allowSubSelection: true,
              filterColumnsType: ['Formula', 'Number'],
              emptyListPlaceholder: 'No numeric fields',
              allowOpenStepsPredicate: preventSelectingColumnForCount,
              backBtnLabel: 'Back to Aggregation Methods',
              stepsTagViewUse: [true],
              nodePredicate: isAggregateNodeForChart,
              placeholder: 'Select Value',
            },
          ),
          // BL-2584
          //   (syncModel: any, value: string) => {
          //     const compareAggregationValue = getValueByPath(syncModel, 'content.data.compareAggregation');
          //     if (value && compareAggregationValue && value === 'FIELD VALUE') {
          //       return [['content.data.compareAggregation', value]];
          //     } else if (value === 'COUNT') {
          //       if (compareAggregationValue === 'FIELD VALUE') {
          //         return [['content.data.compareAggregation', value], ['content.data.mainValue', ''], ['content.data.compareValue', '']];
          //       }
          //       return [['content.data.mainValue', '']];
          //     } else if (value && compareAggregationValue === 'FIELD VALUE' && value !== 'FIELD VALUE') {
          //       return [['content.data.compareAggregation', value]];
          //     }
          //     return [];
          //   },
          // ),
          // epc.utils.conditionalField(
          //   epc.utils.observedField(
          //     epc.fields.selectDataValueField(
          //       'Compare to',
          //       ['content.data.compareAggregation', 'content.data.compareValue'],
          //       [AGGREGATION_FNS_COMPARE_OPTIONS, 'data.?key:value.dataSource'],
          //       {
          //         dataBindingsMode: 'first-dataset',
          //         allowSubSelection: true,
          //         allowNone: true,
          //         filterColumnsType: ['Formula', 'Number'],
          //         emptyListPlaceholder: 'No numeric fields',
          //         allowOpenStepsPredicate: preventSelectingColumnForCount,
          //         backBtnLabel: 'Back to Aggregation Methods',
          //         stepsTagViewUse: [true],
          //         nodePredicate: isAggregateNodeForChart,
          //         placeholder: 'Optional'
          //       },
          //     ),
          //     (syncModel: any, value: string) => {
          //       if (value === 'COUNT') {
          //         return [['content.data.compareValue', '']];
          //       }
          //       return [];
          //     },
          //   ),
          //   'content.data',
          //   value => !value || !value.aggregation || (value.aggregation === 'FIELD VALUE'),
          // ),
          // epc.utils.conditionalField(
          //   epc.fields.selectDataValueField(
          //     'Compare to',
          //     ['content.data.compareAggregation', 'content.data.compareValue'],
          //     [createSelectOptions(['FIELD VALUE']), 'data.?key:value.dataSource'],
          //     {
          //       dataBindingsMode: 'first-dataset',
          //       allowSubSelection: true,
          //       allowNone: true,
          //       filterColumnsType: ['Formula', 'Number'],
          //       emptyListPlaceholder: 'No numeric fields',
          //       allowOpenStepsPredicate: preventSelectingColumnForCount,
          //       backBtnLabel: 'Back to Aggregation Methods',
          //       stepsTagViewUse: [true],
          //       nodePredicate: isAggregateNodeForChart,
          //       placeholder: 'Optional'
          //     },
          //   ),
          //   'content.data',
          //   value => !value || !value.aggregation || value.aggregation !== 'FIELD VALUE',
          // ),
          // epc.utils.conditionalField(
          //   epc.fields.contentAlignField('content.format.delta', 'Delta', {
          //     options: [
          //       { label: 'None', value: 'none' },
          //       { label: '%', value: 'percent' },
          //       { label: 'Value', value: 'value' },
          //     ]
          //   } as any),
          //   'content.data',
          //   value => !value?.aggregation || !value?.compareAggregation,
          // ),
          // epc.utils.separatedField(
          epc.fields.selectFieldFromList(
            'content.format.type',
            'Format',
            NUMBER_FIELD_FORMATS.map((v) => ({ label: v.toString(), value: v.toString() })),
            {
              showSelection: true,
              allowNone: false,
            },
          ),
          // BL-2584
          //   'Format',
          // ),
          epc.fields.currencyConditionalField('content.format.currency', 'content.format.type'),
          epc.fields.numberPrecisionField('content.format.precision'),
          ...epc.fields.linkFields(),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
        },
      ),
      epc.subsections.subsection(
        'data.?key:value.enabled',
        'Alert Style',
        [
          epc.fields.toggleField('content.alert.colorMainValue', 'Color main value', {
            renderType: 'check',
          }),
          epc.fields.toggleField('content.alert.colorChartOutline', 'Color chart outline', {
            renderType: 'check',
          }),
          epc.utils.separatedField(
            epc.fields.contentAlignField('content.alert.logic', '', {
              hideLabel: true,
              options: [
                { label: 'Positive', value: '+' },
                { label: 'Negative', value: '-' },
              ],
            }),
            'Alert is positive when delta is',
          ),
        ],
        {
          hideWhen: (_, __, model: ChartPropsModel) => {
            const comparedFields =
              getValueByPath(model, 'content.data.mainValue') &&
              getValueByPath(model, 'content.data.compareValue');
            return !getValueByPath(model, 'data.?key:value') || !comparedFields;
          },
        },
      ),
      epc.subsections.subsection(
        'content.chart',
        'Add Image',
        [
          epc.fields.uploadField('content.chart.image', 'Type URL or upload image'),
          epc.utils.conditionalField(
            epc.fields.contentAlignField('content.chart.imagePosition', 'Image Position', {
              options: [
                { label: 'Left', value: 'row' },
                { label: 'Right', value: 'row-reverse' },
              ],
            } as any),
            'content.chart.image',
            (model) => !model,
          ),
        ],
        { optional: true, hideWhen: () => true },
      ),

      epc.subsections.subsection(
        'content.misc',
        'Size',
        [
          epc.root.elementWidthField(
            {
              min: SINGLE_CHART_DIMENSIONS.minSize,
              max: SINGLE_CHART_DIMENSIONS.maxWidth,
              unit: 'px',
              step: 1,
            },
            SINGLE_CHART_DIMENSIONS.widthOptions,
          ),
          epc.utils.observedField(
            epc.root.elementHeightField(
              {
                min: SINGLE_CHART_DIMENSIONS.minSize,
                max: SINGLE_CHART_DIMENSIONS.maxHeight,
                unit: 'px',
                step: 1,
              },
              SINGLE_CHART_DIMENSIONS.heightOptions,
            ),
            (model: ChartPropsModel, value: any, syncModel) => {
              console.log({ model, value, syncModel });
              if (
                (getValueByPath(model, 'root.height') as HeightSettings | undefined)?.value !==
                value?.value
              ) {
                return { 'content.chart.height': 'regular' };
              }
              return {};
            },
          ),
        ],
        { hideLabel: true },
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content', [
        epc.fields.textInput('content.title.description', 'Description'),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        {
          path: 'style.general',
          label: 'General',
          items: [
            epc.fields.colorField('style.general.value', 'Value', {
              allowGradient: false,
              allowNone: false,
            }),
          ],
        },
        epc.subsections.elementFontSubsection('style.valueFont', 'Value font', {
          fixedFontSizeOptional: true,
          unitsOrOptions: [{ min: 16, max: 300, unit: 'px' }],
        }),
        epc.subsections.subsection('style.background', 'Background', [
          epc.fields.colorField('style.background.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.background.outline', 'Outline', {
            allowGradient: false,
            allowSolid: true,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.background.width', 'Width', [
            { min: 0, max: 25, unit: 'px' },
          ]),
          epc.fields.radiusField('style.background.radius'),
          ...epc.fields.visualEffectField('style.background.visualEffect'),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          descriptionColor: 'style.general.description',
          fontFamily: 'content.font.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'content.font.style',
          fontSize: 'content.font.fontSize',
          horizontalAlign: 'style.general.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
        // BL-2584
        // epc.subsections.subsection('style.alert', 'Alert', [
        //   epc.fields.colorField('style.alert.neutral', 'Neutral', { allowGradient: false, allowNone: false }),
        //   epc.fields.colorField('style.alert.success', 'Positive', { allowGradient: false, allowNone: false }),
        //   epc.fields.colorField('style.alert.danger', 'Negative', { allowGradient: false, allowNone: false }),
        // ], {
        //   hideWhen: (_, __, model: ChartPropsModel) => {
        //     const comparedFields = getValueByPath(model, 'content.data.mainValue') && getValueByPath(model, 'content.data.compareValue');
        //     return !getValueByPath(model, 'data.?key:value') || !comparedFields;
        //   }
        // }),
      ],
      true,
    ),
  ]);
}

function getDefaults(): ChartValuePropsModel {
  return {
    version: 1,
    content: {
      chart: {
        image: '',
        height: '125px',
      },
      title: {
        label: 'Number',
        description: '',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      format: {
        type: 'Number',
        precision: 0,
        currency: 'USD',
      },
      font: {
        style: [],
        fontSize: '$t.fontSize',
        fontFamily: '$t.headerFontFamily',
        lineHeight: '$t.lineHeight',
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        description: { color1: '$t.value', selectorType: 'solid' },
        value: { color1: '$t.value', selectorType: 'solid' },
        align: 'left',
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingCharts' },
      },
      titleBlock: {
        fontWeight: '400',
        iconSize: '$t.headerIconSize',
      },
      valueFont: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontWeight: '300',
        fontSize: '48px',
      },
      background: {
        fill: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        width: '2px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      // BL-2584
      // alert: {
      //   neutral: { color1: '#F8B918', selectorType: 'solid' },
      //   danger: { color1: '#EF305E', selectorType: 'solid' },
      //   success: { color1: '#6ABA4E', selectorType: 'solid' },
      // }
    },
  };
}

function getInitial(): PartialPropsOf<'chartSingle'> {
  return {
    version: 3,
    content: {
      chart: {
        height: 'regular',
      },
      font: {
        fontSize: '$t.headerFontSize',
        style: '$t.headerFontStyle' as unknown as PropertiesConfigInputFontStyleOptions,
      },
      options: {
        valueMinHeightEnabled: true,
      },
    },
    style: {
      titleBlock: {
        fontWeight: '$t.headerFontWeight',
      },
      background: {
        width: '$t.containerOutlineWidth',
      },
    },
  };
}

function isAggregateNodeForChart(node: WorkflowNode): boolean {
  return !!findWorkflowNode([node], isAggregateNode, 'aggregate', 'chart-');
}
